import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtError, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, PageHeader } from "@/components/ui-kit";
import { Loader } from "@/components/Loader";
import { User, MapPin, Users, HeartHandshake, Bell, Shield, FileText, LogOut, ChevronRight, Camera, BadgeCheck, ScrollText, Radio, Siren, BarChart3, ShieldCheck, Gavel } from "lucide-react";
import { toast } from "sonner";
import { TrustBadge } from "@/components/TrustBadge";

export default function Profile() {
  const { user, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [trust, setTrust] = useState(null);
  const fileRef = useRef();

  useEffect(() => {
    Promise.all([api.get("/trusted-contacts"), api.get("/care-profiles"), api.get("/users/me/community")])
      .then(([tc, cp, cm]) => setStats({ contacts: tc.data.length, care: cp.data.length, community: cm.data.community, membership: cm.data.membership }))
      .catch(() => setStats({ contacts: 0, care: 0 }));
    api.get("/me/trust").then((r) => setTrust(r.data)).catch(() => setTrust(null));
  }, []);

  const changePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload/profile-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { data: updated } = await api.put("/users/me", { profile_photo_url: data.url });
      setUser(updated);
      toast.success("Profile photo updated");
    } catch (err) { toast.error(fmtError(err.response?.data?.detail)); }
  };

  if (!stats) return <Loader />;
  const vs = stats.membership?.verification_status || "unverified";
  const vsColor = { verified: "text-[#16A34A]", pending: "text-[#F59E0B]", unverified: "text-[#111827]/50", rejected: "text-[#DC2626]" }[vs];

  const rows = [
    { i: ShieldCheck, t: "Trust & Verification", v: trust ? `${trust.badge}` : "View", to: "/profile/trust", id: "row-trust" },
    { i: MapPin, t: "Home community", v: stats.community?.name || "Not set", to: "/profile/community", id: "row-community" },
    { i: Users, t: "Trusted contacts", v: `${stats.contacts} added`, to: "/profile/trusted-contacts", id: "row-contacts" },
    { i: HeartHandshake, t: "Care Circle", v: `${stats.care} loved ones`, to: "/care", id: "row-care" },
    { i: Bell, t: "Permissions", v: "Manage", to: "/profile/permissions", id: "row-permissions" },
  ];
  const isAdmin = ["community_admin", "platform_admin"].includes(user?.role);
  const coordination = [
    { i: Radio, t: "Community pulse", v: "Live activity", to: "/community/pulse", id: "row-community-pulse" },
    { i: Siren, t: "Responder inbox", v: "Help neighbours", to: "/responder", id: "row-responder" },
    { i: BarChart3, t: "Analytics", v: "Response insights", to: "/analytics", id: "row-analytics" },
    ...(isAdmin ? [{ i: Gavel, t: "Moderation", v: "Reports & verification", to: "/admin", id: "row-moderation" }] : []),
  ];
  const legal = [
    { t: "Privacy & data controls", to: "/legal/privacy", id: "legal-privacy" },
    { t: "Terms of Service", to: "/legal/terms", id: "legal-terms" },
    { t: "Community Guidelines", to: "/legal/community-guidelines", id: "legal-guidelines" },
    { t: "Safety Disclaimer", to: "/legal/beta-safety", id: "legal-beta" },
  ];

  return (
    <div className="ll-fade-up" data-testid="profile-screen">
      <PageHeader title="Profile" />
      <div className="px-5">
        <Card className="flex items-center gap-4">
          <div className="relative">
            <div className="w-20 h-20 rounded-3xl bg-[#C4B5FD]/30 overflow-hidden flex items-center justify-center">
              {user?.profile_photo_url ? <img src={fileUrl(user.profile_photo_url)} alt="" className="w-full h-full object-cover" /> : <User className="w-9 h-9 text-[#111827]/40" />}
            </div>
            <button data-testid="change-photo" onClick={() => fileRef.current?.click()} className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-[#F97316] text-white flex items-center justify-center shadow-md"><Camera className="w-4 h-4" /></button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={changePhoto} />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-bold truncate">{user?.full_name}</h2>
            <p className="text-[#111827]/50 text-sm truncate">{user?.email}</p>
            <div className="mt-1.5">{trust ? <TrustBadge badge={trust.badge} size="sm" testId="profile-badge" /> : <span className={`text-sm capitalize ${vsColor}`}>{vs}</span>}</div>
          </div>
        </Card>

        {trust && (
          <button data-testid="profile-trust-card" onClick={() => navigate("/profile/trust")} className="w-full mt-4 bg-[#111827] text-white rounded-3xl p-5 flex items-center gap-4 relative overflow-hidden">
            <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full" style={{ background: `${trust.badge_color}44`, filter: "blur(24px)" }} />
            <div className="relative flex-1 text-left">
              <p className="text-xs font-bold uppercase tracking-wider text-white/50">Trust score</p>
              <p className="text-4xl font-black font-head">{trust.trust_score}<span className="text-lg text-white/40">/100</span></p>
              <p className="text-xs text-white/50 mt-0.5">Based on verified actions</p>
            </div>
            <ChevronRight className="w-5 h-5 text-white/60 relative" />
          </button>
        )}

        <div className="mt-4 bg-white rounded-3xl overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-[#111827]/5">
          {rows.map((r) => (
            <button key={r.id} data-testid={r.id} onClick={() => navigate(r.to)} className="w-full flex items-center gap-4 px-5 py-4 border-b border-[#111827]/5 last:border-0 hover:bg-[#FFF7E6] min-h-[56px]">
              <div className="w-10 h-10 rounded-xl bg-[#F97316]/10 flex items-center justify-center"><r.i className="w-5 h-5 text-[#F97316]" /></div>
              <div className="flex-1 text-left"><p className="font-semibold">{r.t}</p><p className="text-sm text-[#111827]/50">{r.v}</p></div>
              <ChevronRight className="w-5 h-5 text-[#111827]/30" />
            </button>
          ))}
        </div>

        <div className="mt-4 bg-white rounded-3xl overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-[#111827]/5">
          <div className="px-5 py-3 flex items-center gap-2 text-[#111827]/50 text-xs font-bold uppercase tracking-wider border-b border-[#111827]/5"><Radio className="w-4 h-4" /> Guardian AI</div>
          {coordination.map((r) => (
            <button key={r.id} data-testid={r.id} onClick={() => navigate(r.to)} className="w-full flex items-center gap-4 px-5 py-4 border-b border-[#111827]/5 last:border-0 hover:bg-[#FFF7E6] min-h-[56px]">
              <div className="w-10 h-10 rounded-xl bg-[#DC2626]/10 flex items-center justify-center"><r.i className="w-5 h-5 text-[#DC2626]" /></div>
              <div className="flex-1 text-left"><p className="font-semibold">{r.t}</p><p className="text-sm text-[#111827]/50">{r.v}</p></div>
              <ChevronRight className="w-5 h-5 text-[#111827]/30" />
            </button>
          ))}
        </div>

        <div className="mt-4 bg-white rounded-3xl overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-[#111827]/5">
          <div className="px-5 py-3 flex items-center gap-2 text-[#111827]/50 text-xs font-bold uppercase tracking-wider border-b border-[#111827]/5"><ScrollText className="w-4 h-4" /> Legal & privacy</div>
          {legal.map((l) => (
            <button key={l.id} data-testid={l.id} onClick={() => navigate(l.to)} className="w-full flex items-center gap-4 px-5 py-4 border-b border-[#111827]/5 last:border-0 hover:bg-[#FFF7E6] min-h-[52px]">
              <FileText className="w-5 h-5 text-[#111827]/40" />
              <span className="flex-1 text-left font-medium">{l.t}</span>
              <ChevronRight className="w-5 h-5 text-[#111827]/30" />
            </button>
          ))}
        </div>

        <button data-testid="sign-out" onClick={async () => { await logout(); navigate("/auth/sign-in", { replace: true }); }} className="w-full mt-5 bg-[#DC2626]/10 text-[#DC2626] rounded-2xl py-4 font-bold flex items-center justify-center gap-2 min-h-[56px]"><LogOut className="w-5 h-5" /> Sign out</button>
        <p className="text-xs text-[#111827]/40 text-center mt-4 mb-2">HumNest</p>
      </div>
    </div>
  );
}
